import React, { useState, useEffect } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import { useAuth } from './AuthContext'
import { useIsMobile } from './useIsMobile'
import { useIdleTimeout } from './useIdleTimeout'
import { CommandPalette } from './CommandPalette'
import PwaInstallBanner from './PwaInstallBanner'
import Login from './Login'
import LandingPage from './LandingPage'
import Dashboard from './Dashboard'
import UserManagement from './UserManagement'
import Catalogo from './Catalogo'
import Financeiro from './Financeiro'
import ComprasFornecedor from './ComprasFornecedor'
import MinhasEncomendas from './MinhasEncomendas'
import Versoes from './Versoes'
import Feedback from './Feedback'
import PrecosCliente from './PrecosCliente'
import HistoricoPrecos from './HistoricoPrecos'
import AnaliseFactura from './AnaliseFactura'
import LucroAnalise from './LucroAnalise'
import PrevisaoFaturacao from './PrevisaoFaturacao'
import IvaPage from './IvaPage'
import Tesouraria from './Tesouraria'
import Mercado from './Mercado'
import VigiaMercado from './VigiaMercado'
import Atividade from './Atividade'
import Comunicacoes from './Comunicacoes'
import ErroGlobal, { ErroBoundary } from './ErroGlobal'
import { TermosUtilizacao, PoliticaDevolucao } from './PaginasLegais'
import { C, FONT, LOGO_LIGHT } from './theme'
import { CopyButton } from './CopyButton'
import {
  Package, AlertTriangle, CheckCircle, Clock, Truck, BarChart2,
  ChevronRight, Save, RefreshCw, Search, LogOut, User, Shield, Users,
  X, Trash2, FileText, ShoppingCart, CreditCard, Send, Eye, EyeOff,
  Lock, GitBranch, Bell, MapPin, Pencil, Menu, MessageSquare,
  DollarSign, ShoppingBag, Plus, Download, Smartphone, Activity, Wallet, Tag, History, TrendingUp, Landmark, Globe,
} from 'lucide-react'

// ── Documentos legais ───────────────────────────────────────────────────────
function TermosCondicoes() {
  return (
    <div>
      <p style={{ fontSize: '12px', color: C.textMuted, marginTop: 0 }}><em>Última atualização: Maio 2026</em></p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '8px', marginBottom: '8px' }}>0. Identificação do Operador</h3>
      <p>A plataforma <strong>LogiStock Pro</strong> é operada por:</p>
      <ul style={{ paddingLeft: '20px', margin: '6px 0' }}>
        <li><strong>Cátia Andreia da Silva</strong> — Empresário em Nome Individual (ENI)</li>
        <li><strong>NIF:</strong> 241 288 398</li>
        <li><strong>Sede:</strong> Avenida da Ponte, n.º 75, 4795-550 Negrelos, Portugal</li>
        <li><strong>Telefone:</strong> +351 963 980 628</li>
        <li><strong>Email:</strong> comercial@lognow.pt</li>
      </ul>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>1. Aceitação dos Termos</h3>
      <p>Ao aceder e utilizar a plataforma LogiStock Pro (doravante "Plataforma"), o utilizador declara ter lido, compreendido e aceitado integralmente os presentes Termos e Condições. Se não concordar, não deve utilizar a Plataforma.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>2. Descrição do Serviço</h3>
      <p>O LogiStock Pro é uma plataforma B2B de gestão de stock e encomendas, destinada a clientes empresariais ou particulares previamente aprovados. Permite consultar catálogo, efetuar encomendas, acompanhar estado de pagamento e gestão de stock, e receber notificações automáticas.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>3. Conta e Acesso</h3>
      <p>O acesso requer registo prévio e aprovação manual pelo Operador. O utilizador é responsável pela confidencialidade da sua palavra-passe e por todas as atividades realizadas na sua conta. Deve notificar imediatamente o Operador de qualquer uso não autorizado.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>4. Encomendas e Pagamento</h3>
      <ul style={{ paddingLeft: '20px', margin: '6px 0' }}>
        <li>As encomendas estão sujeitas a confirmação por parte do Operador.</li>
        <li>As condições de pagamento <strong>dependem do prazo de entrega do artigo</strong>, indicado no catálogo e confirmado no carrinho antes de finalizar a encomenda. Consoante o artigo, o pagamento é feito <strong>a 100% no momento da encomenda</strong> ou <strong>em duas prestações</strong> — 50% no momento da encomenda e os restantes 50% antes do envio.</li>
        <li>Podem aplicar-se <strong>valores mínimos de encomenda</strong> distintos consoante o prazo de entrega do artigo. O valor mínimo em vigor é apresentado no carrinho.</li>
        <li>Os <strong>portes de envio estão incluídos no preço</strong> de cada produto — sem custos adicionais na expedição.</li>
        <li>Os preços apresentados são em Euros, com indicação separada de IVA quando aplicável.</li>
        <li>O incumprimento do pagamento da segunda prestação no prazo indicado pode levar ao cancelamento da encomenda.</li>
      </ul>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>5. Cancelamentos e Devoluções</h3>
      <p>O cancelamento de encomendas pode ser solicitado antes da expedição. Devoluções de produto após receção seguem a legislação portuguesa em vigor, sendo necessário contactar o administrador da Plataforma para iniciar o processo.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>6. Limitação de Responsabilidade</h3>
      <p>A Plataforma é fornecida no estado em que se encontra. Embora envidemos esforços para manter o serviço disponível e os dados atualizados, não garantimos ausência total de erros, indisponibilidade temporária ou correção absoluta de informação (preços, stocks). A responsabilidade limita-se ao valor da encomenda em causa.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>7. Propriedade Intelectual</h3>
      <p>Todo o conteúdo da Plataforma (interface, marca LogiStock Pro, código, textos, gráficos) é propriedade do operador e está protegido pela legislação aplicável. É proibida qualquer reprodução, modificação ou utilização sem autorização escrita.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>8. Alterações aos Termos</h3>
      <p>O operador reserva-se o direito de alterar estes Termos a qualquer momento. As alterações entram em vigor após publicação na Plataforma. O uso continuado da Plataforma após alterações constitui aceitação dos novos Termos.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>9. Lei Aplicável</h3>
      <p>Os presentes Termos regem-se pela lei portuguesa. Qualquer litígio será resolvido nos tribunais competentes de Portugal.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>10. Reclamações</h3>
      <p>Em caso de reclamação, pode utilizar o <a href="https://www.livroreclamacoes.pt/" target="_blank" rel="noopener noreferrer" style={{ color: C.text, textDecoration: 'underline' }}>Livro de Reclamações Eletrónico</a> ou recorrer à resolução alternativa de litígios através do <a href="https://www.cniacc.pt" target="_blank" rel="noopener noreferrer" style={{ color: C.text, textDecoration: 'underline' }}>CNIACC</a>.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>11. Contacto</h3>
      <p>Para questões relacionadas com estes Termos, contacte o Operador através do email <strong>comercial@lognow.pt</strong> ou telefone <strong>+351 963 980 628</strong>.</p>
    </div>
  )
}

function PoliticaPrivacidade() {
  return (
    <div>
      <p style={{ fontSize: '12px', color: C.textMuted, marginTop: 0 }}><em>Última atualização: Maio 2026</em></p>
      <p>A presente Política de Privacidade descreve como o LogiStock Pro recolhe, utiliza, armazena e protege os seus dados pessoais, em conformidade com o Regulamento Geral de Proteção de Dados (RGPD - Regulamento UE 2016/679).</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>1. Responsável pelo Tratamento</h3>
      <p>O responsável pelo tratamento dos seus dados pessoais é:</p>
      <ul style={{ paddingLeft: '20px', margin: '6px 0' }}>
        <li><strong>Cátia Andreia da Silva</strong> — Empresário em Nome Individual (ENI)</li>
        <li><strong>NIF:</strong> 241 288 398</li>
        <li><strong>Sede:</strong> Avenida da Ponte, n.º 75, 4795-550 Negrelos, Portugal</li>
        <li><strong>Contacto:</strong> comercial@lognow.pt · +351 963 980 628</li>
      </ul>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>2. Dados Recolhidos</h3>
      <ul style={{ paddingLeft: '20px', margin: '6px 0' }}>
        <li><strong>Identificação</strong>: nome, email, telefone, NIF, morada e código postal</li>
        <li><strong>Conta</strong>: palavra-passe (encriptada), data de criação, estado da conta</li>
        <li><strong>Encomendas</strong>: histórico, valores, estado de pagamento, dados de entrega</li>
        <li><strong>Técnicos</strong>: tokens de sessão, registos de acesso (logs), IPs</li>
      </ul>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>3. Finalidades</h3>
      <ul style={{ paddingLeft: '20px', margin: '6px 0' }}>
        <li>Gestão da conta e autenticação</li>
        <li>Processamento de encomendas e pagamentos</li>
        <li>Envio de notificações sobre encomendas, alertas de stock e atualizações</li>
        <li>Cumprimento de obrigações legais e contabilísticas</li>
        <li>Melhoria contínua do serviço</li>
      </ul>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>4. Fundamentos Legais</h3>
      <p>Os dados são tratados com base na <strong>execução do contrato</strong> (encomendas e gestão de conta), em <strong>obrigações legais</strong> (faturação, contabilidade) e no <strong>interesse legítimo</strong> em melhorar o serviço.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>5. Partilha com Terceiros</h3>
      <p>Os dados são partilhados apenas com prestadores essenciais ao serviço:</p>
      <ul style={{ paddingLeft: '20px', margin: '6px 0' }}>
        <li><strong>Supabase</strong> (alojamento da base de dados e autenticação)</li>
        <li><strong>Servidor SMTP</strong> (envio de emails transacionais)</li>
        <li><strong>Transportadoras</strong> (CTT, DHL, etc., apenas dados de entrega)</li>
      </ul>
      <p>Não vendemos nem cedemos os seus dados para fins comerciais a terceiros.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>6. Conservação dos Dados</h3>
      <p>Os dados pessoais são conservados pelo período necessário ao cumprimento das finalidades indicadas e das obrigações legais (mínimo de 10 anos para faturação, ao abrigo do CIVA).</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>7. Direitos do Titular</h3>
      <p>O utilizador pode, a qualquer momento, exercer os seguintes direitos:</p>
      <ul style={{ paddingLeft: '20px', margin: '6px 0' }}>
        <li><strong>Acesso</strong> aos seus dados pessoais</li>
        <li><strong>Retificação</strong> de dados incorretos ou desatualizados</li>
        <li><strong>Apagamento</strong> ("direito ao esquecimento"), nos limites legais</li>
        <li><strong>Limitação</strong> e <strong>oposição</strong> ao tratamento</li>
        <li><strong>Portabilidade</strong> dos seus dados</li>
      </ul>
      <p>Para exercer estes direitos, contacte-nos pela Plataforma. Pode também apresentar reclamação à Comissão Nacional de Proteção de Dados (CNPD).</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>8. Segurança</h3>
      <p>Aplicamos medidas técnicas e organizacionais adequadas: encriptação de palavras-passe, RLS (Row-Level Security) em todas as tabelas, comunicações HTTPS, acesso restrito a administradores autorizados.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>9. Cookies e Tecnologias Semelhantes</h3>
      <p>A Plataforma utiliza cookies essenciais para autenticação e funcionamento (session tokens). Não utiliza cookies de marketing ou rastreamento publicitário de terceiros.</p>

      <h3 style={{ fontSize: '15px', fontWeight: '700', marginTop: '18px', marginBottom: '8px' }}>10. Alterações à Política</h3>
      <p>Esta Política pode ser atualizada periodicamente. As alterações relevantes serão comunicadas aos utilizadores ativos.</p>
    </div>
  )
}

const VALID_PAGES = new Set([
  'dashboard','catalogo','minhas-encomendas','financeiro','compras','lucro','iva','tesouraria',
  'termos','politica-devolucao','precos-cliente','historico-precos','mercado','precos-mercado','analise-factura','previsao','utilizadores','comunicacoes','atividade','feedback',
])

function readHashPage() {
  if (typeof window === 'undefined') return null
  const h = window.location.hash.replace(/^#\/?/, '')
  return VALID_PAGES.has(h) ? h : null
}

export default function App() {
  const { user, perfil, loading, isAdmin, isCliente, signOut, loadPerfil, viewAsEmail, encomendarComoEmail, setEncomendarComoEmail, dadosIncompletos, inPasswordRecovery } = useAuth()
  const [encomendarComoPerfil, setEncomendarComoPerfil] = React.useState(null)
  React.useEffect(() => {
    if (!encomendarComoEmail) { setEncomendarComoPerfil(null); return }
    let cancelado = false
    supabase.from('perfis').select('nome, email').eq('email', encomendarComoEmail).maybeSingle()
      .then(({ data }) => { if (!cancelado) setEncomendarComoPerfil(data || null) })
    return () => { cancelado = true }
  }, [encomendarComoEmail])
  const sairModoEncomendar = () => {
    try {
      const carrinho = JSON.parse(localStorage.getItem('carrinho') || '[]')
      if (Array.isArray(carrinho) && carrinho.length > 0) {
        if (!confirm('Sair do modo "encomendar como" vai limpar o carrinho. Continuar?')) return
        localStorage.removeItem('carrinho')
        window.dispatchEvent(new Event('storage'))
      }
    } catch { /* ignore */ }
    setEncomendarComoEmail(null)
  }
  const isMobile = useIsMobile()
  const [activePage, setActivePage] = useState(() => readHashPage() || 'dashboard')

  // Nº de artigos no carrinho, para o ícone da barra do topo.
  // O carrinho vive no localStorage e o Catálogo/Gestão de Stock avisam de
  // alterações com window.dispatchEvent(new Event('storage')) — é essa a convenção
  // já usada na app, por isso basta ouvi-la (o evento nativo só dispara noutra aba).
  const [carrinhoQtd, setCarrinhoQtd] = useState(0)
  React.useEffect(() => {
    const ler = () => {
      try {
        const arr = JSON.parse(localStorage.getItem('carrinho') || '[]')
        setCarrinhoQtd(Array.isArray(arr) ? arr.reduce((s, it) => s + (Number(it?.quantidade) || 0), 0) : 0)
      } catch { setCarrinhoQtd(0) }
    }
    ler()
    window.addEventListener('storage', ler)
    return () => window.removeEventListener('storage', ler)
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    // O magic link (entrar como cliente), a recuperação de password e a
    // confirmação de email devolvem a SESSÃO NO HASH: #access_token=…
    // Como isso não é uma página válida, o readHashPage devolve null, o
    // activePage fica no default e este efeito reescrevia o hash para
    // "#dashboard" — apagando o token antes de o supabase-js o ler. Resultado:
    // o magic link levava ao ecrã de login, como se não tivesse funcionado.
    // Enquanto o hash for uma resposta de autenticação, não lhe tocamos; o
    // supabase-js limpa-o sozinho depois de criar a sessão.
    const h = window.location.hash
    if (/access_token=|refresh_token=|error_description=|type=(recovery|magiclink|signup|invite)/.test(h)) return
    const targetHash = `#${activePage}`
    if (h !== targetHash) {
      window.history.replaceState(null, '', targetHash)
    }
    // user?.id nas dependências: depois de a sessão ser criada e o supabase-js
    // limpar o hash, isto volta a correr e repõe o "#dashboard".
  }, [activePage, user?.id])

  React.useEffect(() => {
    const onHash = () => {
      const p = readHashPage()
      if (p) setActivePage(p)
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  // Factura a abrir na Analise Factura quando se vem do Lucro Real.
  //
  // O Lucro Real diz QUANTO uma factura rendeu; a Analise Factura diz PORQUE.
  // Sao resumo e detalhe do mesmo documento, e ate agora nao se falavam: quando
  // um numero surpreendia, era sair, mudar de separador e procurar a factura
  // outra vez a mao. Era isso que as fazia parecer repetidas.
  //
  // Estado no App e nao no endereco: o efeito que sincroniza o hash reescreve-o
  // para `#pagina` sempre que difere, e apagaria qualquer parametro que la
  // puséssemos.
  const [analiseCompraId, setAnaliseCompraId] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [cmdkOpen, setCmdkOpen] = useState(false)
  const [versoesModalOpen, setVersoesModalOpen] = useState(false)

  // Cliente entra direto no Catálogo; admin fica em Gestão de Stock.
  // Se o URL já tem hash válido (refresh), respeita esse — não força redirect.
  const landedRef = React.useRef(false)
  React.useEffect(() => {
    if (!perfil || landedRef.current) return
    landedRef.current = true
    if (readHashPage()) return
    if (perfil.role === 'cliente' && !viewAsEmail) {
      setActivePage('catalogo')
    }
  }, [perfil, viewAsEmail])

  // Modal de boas-vindas no primeiro acesso do cliente
  const [showWelcome, setShowWelcome] = useState(false)
  React.useEffect(() => {
    if (perfil && perfil.role === 'cliente' && !viewAsEmail && !perfil.welcome_seen_at) {
      const t = setTimeout(() => setShowWelcome(true), 600)
      return () => clearTimeout(t)
    }
  }, [perfil, viewAsEmail])

  // O SEPARADOR DE PRECOS DE MERCADO E UM INTERRUPTOR POR CLIENTE.
  //
  // Nao basta ter artigos na lista: e o cliente que os escolhe, por isso o
  // separador tem de existir VAZIO, senao ele nao tem onde escolher o
  // primeiro. `perfis.precos_mercado` e ligado a mao pelo Marco — hoje so o
  // Ruben. Assim nao ha emails escritos no codigo.
  //
  // EM "VER COMO CLIENTE" O PERFIL CARREGADO CONTINUA A SER O DO ADMIN. Sem
  // este pedaco, o separador nao aparecia no ver-como e nao havia maneira de o
  // experimentar sem pedir a password do cliente. Le-se o interruptor e o id de
  // quem se esta a ver, e e esse id que filtra as linhas — porque as regras de
  // acesso deixam o admin ver as de toda a gente, e sem filtro ele via a lista
  // de todos os clientes de uma vez.
  const inViewAs = isAdmin && !!viewAsEmail
  const [vistoComo, setVistoComo] = useState(null)
  React.useEffect(() => {
    let vivo = true
    ;(async () => {
      if (!viewAsEmail) { setVistoComo(null); return }
      const { data } = await supabase.from('perfis')
        .select('id, precos_mercado').ilike('email', viewAsEmail).maybeSingle()
      if (vivo) setVistoComo(data || null)
    })()
    return () => { vivo = false }
  }, [viewAsEmail])

  const temPrecosMercado = inViewAs ? !!vistoComo?.precos_mercado : !!perfil?.precos_mercado

  const fecharWelcome = async () => {
    setShowWelcome(false)
    if (user) {
      const ts = new Date().toISOString()
      await supabase.from('perfis').update({ welcome_seen_at: ts }).eq('id', user.id)
    }
  }

  // Atalho global Ctrl+K — pesquisa (só admin)
  React.useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (isAdmin) setCmdkOpen(true)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isAdmin])

  // Routing público (landing vs login)
  const [pathname, setPathname] = React.useState(typeof window !== 'undefined' ? window.location.pathname : '/')
  React.useEffect(() => {
    const onPop = () => setPathname(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Logout automático após 30 min de inatividade
  const idle = useIdleTimeout({
    enabled: !!user && !inPasswordRecovery,
    timeoutMs: 30 * 60 * 1000,
    warningMs: 60 * 1000,
    onTimeout: () => signOut(),
  })

  const [showPerfil, setShowPerfil] = useState(false)
  const [showDadosIncompletos, setShowDadosIncompletos] = useState(false)
  const [perfilForm, setPerfilForm] = useState({ nome: '', telefone: '', morada: '', email: '', cod_postal: '' })
  // Confirmação por código: { aberto, valor, enviadoPara, erro }
  const [perfilCodigo, setPerfilCodigo] = useState({ aberto: false, valor: '', enviadoPara: '', erro: '' })
  const [perfilSaving, setPerfilSaving] = useState(false)
  const [perfilMsg, setPerfilMsg] = useState({ tipo: '', texto: '' })
  const [passForm, setPassForm] = useState({ nova: '', confirmar: '' })
  const [passShow, setPassShow] = useState({ nova: false, confirmar: false })
  const [passSaving, setPassSaving] = useState(false)
  const [passMsg, setPassMsg] = useState({ tipo: '', texto: '' })

  // Moradas adicionais
  const [moradas, setMoradas] = useState([])
  const [moradasLoading, setMoradasLoading] = useState(false)
  const EMPTY_MORADA = { etiqueta: '', nome: '', nif: '', morada: '', cod_postal: '', pais: 'Portugal', telefone: '' }
  const [moradaForm, setMoradaForm] = useState(EMPTY_MORADA)
  const [moradaEditId, setMoradaEditId] = useState(null)
  const [moradaSaving, setMoradaSaving] = useState(false)
  const [moradaMsg, setMoradaMsg] = useState({ tipo: '', texto: '' })

  const carregarMoradas = async () => {
    if (!user) return
    setMoradasLoading(true)
    const { data } = await supabase.from('cliente_moradas')
      .select('*').eq('cliente_id', user.id).order('created_at', { ascending: true })
    setMoradas(data || [])
    setMoradasLoading(false)
  }

  const guardarMorada = async () => {
    if (!moradaForm.etiqueta.trim() || !moradaForm.nome.trim() || !moradaForm.morada.trim() || !moradaForm.cod_postal.trim() || !moradaForm.pais.trim()) {
      setMoradaMsg({ tipo: 'erro', texto: 'Preencha Etiqueta, Nome, Morada, Código Postal e País.' }); return
    }
    setMoradaSaving(true)
    setMoradaMsg({ tipo: '', texto: '' })
    try {
      const payload = {
        cliente_id: user.id,
        etiqueta: moradaForm.etiqueta.trim(),
        nome: moradaForm.nome.trim(),
        nif: moradaForm.nif.trim() || null,
        morada: moradaForm.morada.trim(),
        cod_postal: moradaForm.cod_postal.trim(),
        pais: moradaForm.pais.trim(),
        telefone: moradaForm.telefone.trim() || null,
      }
      let error
      if (moradaEditId) {
        ({ error } = await supabase.from('cliente_moradas').update(payload).eq('id', moradaEditId))
      } else {
        ({ error } = await supabase.from('cliente_moradas').insert(payload))
      }
      if (error) throw error
      setMoradaForm(EMPTY_MORADA)
      setMoradaEditId(null)
      await carregarMoradas()
      setMoradaMsg({ tipo: 'ok', texto: moradaEditId ? 'Morada atualizada.' : 'Morada adicionada.' })
      setTimeout(() => setMoradaMsg({ tipo: '', texto: '' }), 3000)
    } catch (err) {
      setMoradaMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setMoradaSaving(false)
    }
  }

  const editarMorada = (m) => {
    setMoradaForm({
      etiqueta: m.etiqueta || '', nome: m.nome || '', nif: m.nif || '',
      morada: m.morada || '', cod_postal: m.cod_postal || '',
      pais: m.pais || 'Portugal', telefone: m.telefone || '',
    })
    setMoradaEditId(m.id)
    setMoradaMsg({ tipo: '', texto: '' })
  }

  const eliminarMorada = async (id) => {
    if (!window.confirm('Eliminar esta morada?')) return
    const { error } = await supabase.from('cliente_moradas').delete().eq('id', id)
    if (error) {
      setMoradaMsg({ tipo: 'erro', texto: 'Erro: ' + error.message })
    } else {
      await carregarMoradas()
      if (moradaEditId === id) { setMoradaForm(EMPTY_MORADA); setMoradaEditId(null) }
    }
  }

  // Notificações
  const [notificacoes, setNotificacoes] = useState([])
  const [notifAberto, setNotifAberto] = useState(false)
  const [notifExpandidaId, setNotifExpandidaId] = useState(null)
  const notifNaoLidas = notificacoes.filter(n => !n.lida).length

  const carregarNotificacoes = async () => {
    if (!user) return
    const { data } = await supabase.from('notificacoes')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30)
    setNotificacoes(data || [])
  }

  React.useEffect(() => {
    if (!user) return
    carregarNotificacoes()
    const interval = setInterval(carregarNotificacoes, 30000)

    // Realtime: novas notificacoes aparecem instantaneamente
    const channel = supabase
      .channel(`notif-${user.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notificacoes', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const nova = payload.new
          setNotificacoes(prev => prev.some(n => n.id === nova.id) ? prev : [nova, ...prev].slice(0, 30))
        })
      .subscribe()

    return () => {
      clearInterval(interval)
      supabase.removeChannel(channel)
    }
  }, [user])

  // ── Tracking de visitas (edge function) — defensivo, nunca crasha o site ──
  React.useEffect(() => {
    if (!user) return
    let parado = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token || parado) return
        fetch(`${SUPABASE_URL}/functions/v1/registar-passagem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ path: activePage }),
        }).catch(() => {})
      } catch { /* silencioso */ }
    })()
    return () => { parado = true }
  }, [user, activePage])

  // ── Presence (online users) — defensivo, nunca crasha o site ──
  const presenceChRef = React.useRef(null)
  React.useEffect(() => {
    if (!user) return
    let ch
    try {
      ch = supabase.channel('online-users', { config: { presence: { key: user.id } } })
      presenceChRef.current = ch
      ch.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          ch.track({
            user_id: user.id,
            email: perfil?.email || user.email,
            nome: perfil?.nome || '',
            is_admin: !!isAdmin,
            path: activePage,
            since: new Date().toISOString(),
          }).catch(() => {})
        }
      })
    } catch { /* silencioso */ }
    return () => {
      try { if (ch) supabase.removeChannel(ch) } catch { /* silencioso */ }
      presenceChRef.current = null
    }
  }, [user?.id])

  // Actualizar presence quando muda de pagina/perfil
  React.useEffect(() => {
    if (!user) return
    const ch = presenceChRef.current
    if (!ch) return
    try {
      ch.track({
        user_id: user.id,
        email: perfil?.email || user.email,
        nome: perfil?.nome || '',
        is_admin: !!isAdmin,
        path: activePage,
        since: new Date().toISOString(),
      })?.catch?.(() => {})
    } catch { /* silencioso */ }
  }, [activePage, perfil?.email, perfil?.nome, isAdmin])

  const marcarNotifLida = async (id) => {
    await supabase.from('notificacoes').update({ lida: true, lida_em: new Date().toISOString() }).eq('id', id)
    setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n))
  }

  const marcarTodasLidas = async () => {
    if (!user) return
    await supabase.from('notificacoes').update({ lida: true, lida_em: new Date().toISOString() }).eq('user_id', user.id).eq('lida', false)
    setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })))
  }

  const apagarNotificacao = async (id, ev) => {
    ev?.stopPropagation()
    await supabase.from('notificacoes').delete().eq('id', id)
    setNotificacoes(prev => prev.filter(n => n.id !== id))
  }

  const clicarNotificacao = (n) => {
    if (!n.lida) marcarNotifLida(n.id)
    // Determinar pagina de destino. Prioridade: ref_table (mais preciso) -> link
    let destino = (n.link || '').split(':')[0] || null
    if (n.ref_table === 'Produtos') {
      destino = 'catalogo'
    } else if (n.ref_table === 'perfis') {
      destino = 'utilizadores'
    } else if (n.ref_table === 'encomendas') {
      // Admin: financeiro (modal pagamento). Cliente: minhas-encomendas
      destino = isAdmin ? 'financeiro' : 'minhas-encomendas'
    }
    // Passar ref via sessionStorage para a pagina abrir o item certo
    if (n.ref_table && n.ref_id) {
      try {
        sessionStorage.setItem('notif_open', JSON.stringify({ table: n.ref_table, id: n.ref_id, tipo: n.tipo, ts: Date.now() }))
      } catch (_) { /* sessionStorage indisponivel - tudo bem, segue navegacao normal */ }
    }
    if (destino) setActivePage(destino)
    setNotifAberto(false)
  }

  const fmtTempo = (data) => {
    const agora = Date.now()
    const t = new Date(data).getTime()
    const diff = Math.max(0, agora - t)
    const min = Math.floor(diff / 60000)
    if (min < 1) return 'agora'
    if (min < 60) return `${min}m`
    const horas = Math.floor(min / 60)
    if (horas < 24) return `${horas}h`
    const dias = Math.floor(horas / 24)
    if (dias < 7) return `${dias}d`
    return new Date(data).toLocaleDateString('pt-PT')
  }

  // Modais legais e de feedback
  const [legalModal, setLegalModal] = useState(null)
  const [feedbackModal, setFeedbackModal] = useState(false)
  const [feedbackTab, setFeedbackTab] = useState('enviar')
  const [feedbackForm, setFeedbackForm] = useState({ tipo: 'sugestao', titulo: '', descricao: '' })
  const [feedbackEnviando, setFeedbackEnviando] = useState(false)
  const [feedbackMsg, setFeedbackMsg] = useState({ tipo: '', texto: '' })
  const [meusFeedbacks, setMeusFeedbacks] = useState([])
  const [feedbackNovidades, setFeedbackNovidades] = useState(0)

  const carregarMeusFeedbacks = React.useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('feedback')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
    setMeusFeedbacks(data || [])
    const novidades = (data || []).filter(f => f.estado_atualizado_em && (!f.cliente_viu_em || new Date(f.cliente_viu_em) < new Date(f.estado_atualizado_em))).length
    setFeedbackNovidades(novidades)
  }, [user])

  React.useEffect(() => {
    if (user && !isAdmin) carregarMeusFeedbacks()
  }, [user, isAdmin, carregarMeusFeedbacks])

  const marcarFeedbacksVistos = async () => {
    if (!user || feedbackNovidades === 0) return
    await supabase.from('feedback')
      .update({ cliente_viu_em: new Date().toISOString() })
      .eq('user_id', user.id)
    setFeedbackNovidades(0)
    await carregarMeusFeedbacks()
  }

  const FEEDBACK_ESTADOS = {
    recebido:         { label: 'Recebido',         emoji: '🆕', color: C.textMuted, bg: C.surfaceAlt },
    em_analise:       { label: 'Em análise',       emoji: '👁️', color: C.info,      bg: C.infoSoft },
    em_implementacao: { label: 'Em implementação', emoji: '🔧', color: C.warning,   bg: C.warningSoft },
    concluido:        { label: 'Concluído',        emoji: '✅', color: C.success,   bg: C.successSoft },
    nao_aplicavel:    { label: 'Não aplicável',    emoji: '❌', color: C.danger,    bg: C.dangerSoft },
  }

  React.useEffect(() => {
    if (perfil) setPerfilForm({ nome: perfil.nome || '', telefone: perfil.telefone || '', morada: perfil.morada || '', email: perfil.email || '', cod_postal: perfil.cod_postal || '' })
  }, [perfil])

  const enviarFeedback = async () => {
    if (!feedbackForm.titulo.trim() || !feedbackForm.descricao.trim()) {
      setFeedbackMsg({ tipo: 'erro', texto: 'Título e descrição são obrigatórios.' })
      return
    }
    setFeedbackEnviando(true)
    setFeedbackMsg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/enviar-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify(feedbackForm),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'Erro a enviar')
      setFeedbackMsg({ tipo: 'ok', texto: 'Obrigado! O teu feedback foi enviado com sucesso.' })
      setFeedbackForm({ tipo: 'sugestao', titulo: '', descricao: '' })
      setTimeout(() => { setFeedbackModal(false); setFeedbackMsg({ tipo: '', texto: '' }) }, 2000)
    } catch (e) {
      setFeedbackMsg({ tipo: 'erro', texto: 'Erro: ' + (e?.message || String(e)) })
    } finally {
      setFeedbackEnviando(false)
    }
  }

  React.useEffect(() => {
    if (dadosIncompletos && isCliente) {
      setShowDadosIncompletos(true)
    }
  }, [dadosIncompletos, isCliente])

  React.useEffect(() => {
    if (showPerfil && user) {
      carregarMoradas()
      setMoradaForm(EMPTY_MORADA)
      setMoradaEditId(null)
      setMoradaMsg({ tipo: '', texto: '' })
    }
  }, [showPerfil, user])

  const alterarPassword = async () => {
    if (!passForm.nova || !passForm.confirmar) {
      setPassMsg({ tipo: 'erro', texto: 'Preencha os dois campos.' }); return
    }
    if (passForm.nova.length < 6) {
      setPassMsg({ tipo: 'erro', texto: 'A password deve ter pelo menos 6 caracteres.' }); return
    }
    if (passForm.nova !== passForm.confirmar) {
      setPassMsg({ tipo: 'erro', texto: 'As passwords não coincidem.' }); return
    }
    setPassSaving(true)
    setPassMsg({ tipo: '', texto: '' })
    const { error } = await supabase.auth.updateUser({ password: passForm.nova })
    if (error) {
      setPassMsg({ tipo: 'erro', texto: 'Erro: ' + error.message })
    } else {
      setPassMsg({ tipo: 'ok', texto: 'Password alterada com sucesso.' })
      setPassForm({ nova: '', confirmar: '' })
      setTimeout(() => setPassMsg({ tipo: '', texto: '' }), 5000)
    }
    setPassSaving(false)
  }

  const guardarPerfil = async () => {
    if (!perfilForm.nome.trim() || !perfilForm.telefone.trim() || !perfilForm.morada.trim() || !perfilForm.cod_postal.trim()) {
      setPerfilMsg({ tipo: 'erro', texto: 'Nome, Telefone, Morada e Código Postal são obrigatórios.' }); return
    }
    const novo = {
      nome: perfilForm.nome.trim(),
      telefone: perfilForm.telefone.trim(),
      morada: perfilForm.morada.trim(),
      cod_postal: perfilForm.cod_postal.trim(),
    }
    const alterados = Object.keys(novo).filter(k => String(perfil?.[k] ?? '').trim() !== novo[k])
    if (alterados.length === 0) { setPerfilMsg({ tipo: 'ok', texto: 'Não há alterações para guardar.' }); return }

    // PRIMEIRO PREENCHIMENTO vs ALTERAÇÃO. O código serve para impedir que
    // alguém MUDE contactos existentes; num campo vazio não há nada a proteger,
    // e obrigar um cliente novo a ir buscar um código ao email no primeiro
    // acesso é atrito onde não há risco.
    // A mesma regra vive no trigger da BD (perfil_primeiro_preenchimento.sql) —
    // se estivesse só aqui, a base de dados revertia e o ecrã dizia "guardado".
    const soPreenchimento = alterados.every(k => String(perfil?.[k] ?? '').trim() === '')
    if (soPreenchimento) {
      setPerfilSaving(true)
      setPerfilMsg({ tipo: '', texto: '' })
      const { error } = await supabase.from('perfis').update(novo).eq('id', user.id)
      if (error) {
        setPerfilMsg({ tipo: 'erro', texto: 'Erro ao guardar: ' + error.message })
      } else {
        setPerfilMsg({ tipo: 'ok', texto: 'Perfil guardado.' })
        setTimeout(() => setPerfilMsg({ tipo: '', texto: '' }), 4000)
        // Recarregar: sem isto o `perfil` em memória continua com os campos
        // vazios, e uma segunda alteração seguida voltava a tomar o caminho
        // directo — que a base de dados já recusaria, em silêncio.
        try { await loadPerfil?.(user.id) } catch (_) { /* nao bloqueia */ }
      }
      setPerfilSaving(false)
      return
    }

    // Alterar dados de contacto exige um código enviado por email. O cliente
    // deixou de ter escrita directa nestas colunas (ver
    // supabase/sql/perfil_codigo_confirmacao.sql) — este é o único caminho, e
    // é por isso que o código é uma barreira e não um passo decorativo no ecrã.
    setPerfilSaving(true)
    setPerfilMsg({ tipo: '', texto: '' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/perfil-pedir-codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ alteracoes: novo }),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'Não foi possível enviar o código.')
      setPerfilCodigo({ aberto: true, valor: '', enviadoPara: data.email, erro: '' })
      setPerfilMsg({ tipo: '', texto: '' })
    } catch (err) {
      setPerfilMsg({ tipo: 'erro', texto: err?.message || String(err) })
    }
    setPerfilSaving(false)
  }

  const confirmarCodigoPerfil = async () => {
    const cod = (perfilCodigo.valor || '').replace(/\D/g, '')
    if (cod.length !== 6) { setPerfilCodigo(c => ({ ...c, erro: 'O código tem 6 dígitos.' })); return }
    setPerfilSaving(true)
    setPerfilCodigo(c => ({ ...c, erro: '' }))
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/perfil-confirmar-codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ codigo: cod }),
      })
      const data = await res.json()
      if (!data.ok) { setPerfilCodigo(c => ({ ...c, erro: data.erro || 'Código inválido.' })); setPerfilSaving(false); return }
      setPerfilCodigo({ aberto: false, valor: '', enviadoPara: '', erro: '' })
      setPerfilMsg({ tipo: 'ok', texto: 'Perfil atualizado com sucesso.' })
      setTimeout(() => setPerfilMsg({ tipo: '', texto: '' }), 4000)
      // A alteração foi feita pela edge function; o perfil em memória ficou velho.
      try { await loadPerfil?.(user.id) } catch (_) { /* nao bloqueia */ }
    } catch (err) {
      setPerfilCodigo(c => ({ ...c, erro: err?.message || String(err) }))
    }
    setPerfilSaving(false)
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: C.bg, fontFamily: FONT }}>
        <div style={{ textAlign: 'center', color: C.textMuted }}>
          <BarChart2 size={32} color={C.text} style={{ marginBottom: '12px' }} />
          <p style={{ fontSize: '14px' }}>A carregar...</p>
        </div>
      </div>
    )
  }

  if (!user || inPasswordRecovery) {
    const showLogin = inPasswordRecovery || pathname === '/login' || pathname === '/login/'
    if (showLogin) return <Login />
    return <LandingPage onShowLogin={() => {
      window.history.pushState({}, '', '/login')
      setPathname('/login')
    }} />
  }

  const showAsAdmin = isAdmin && !inViewAs
  const showAsCliente = isCliente || inViewAs

  const navItems = [
    { id: 'dashboard',         label: 'Gestão de Stock',   icon: BarChart2,     show: true,          adminOnly: false, grupo: 'operacao'   },
    { id: 'catalogo',          label: 'Catálogo',          icon: ShoppingCart,  show: true,          adminOnly: false, grupo: 'operacao'   },
    { id: 'minhas-encomendas', label: 'Minhas Encomendas', icon: Package,       show: showAsCliente, adminOnly: false, grupo: 'operacao'   },
    { id: 'precos-mercado',    label: 'Preços de Mercado', icon: Globe,        show: showAsCliente && temPrecosMercado, adminOnly: false, grupo: 'operacao'   },
    { id: 'financeiro',        label: 'Financeiro',        icon: CreditCard,    show: showAsAdmin,   adminOnly: true,  grupo: 'financeiro' },
    { id: 'compras',           label: 'Compras',           icon: ShoppingBag,   show: showAsAdmin,   adminOnly: true,  grupo: 'financeiro' },
    { id: 'lucro',             label: 'Lucro',             icon: DollarSign,    show: showAsAdmin,   adminOnly: true,  grupo: 'financeiro' },
    { id: 'previsao',          label: 'Previsão',          icon: TrendingUp,    show: showAsAdmin,   adminOnly: true,  grupo: 'financeiro' },
    { id: 'iva',               label: 'IVA',               icon: Landmark,      show: showAsAdmin,   adminOnly: true,  grupo: 'financeiro' },
    { id: 'tesouraria',        label: 'Tesouraria',        icon: Wallet,        show: showAsAdmin,   adminOnly: true,  grupo: 'financeiro' },
    { id: 'precos-cliente',    label: 'Preços Cliente',    icon: Tag,           show: showAsAdmin,   adminOnly: true,  grupo: 'analise'    },
    { id: 'historico-precos',  label: 'Histórico Preços',  icon: History,       show: showAsAdmin,   adminOnly: true,  grupo: 'analise'    },
    { id: 'mercado',           label: 'Mercado',           icon: Globe,         show: showAsAdmin,   adminOnly: true,  grupo: 'analise'    },
    // A ANALISE FACTURA NAO TEM ENTRADA NO MENU (06/09/2026).
    // E o detalhe de uma factura; a lista dela e o Lucro > "Lucro real por
    // factura", que ja levava para ca. Ter as duas metades da mesma pergunta em
    // menus diferentes so obrigava a procurar. A rota mantem-se e o botao de
    // voltar leva ao Lucro.
    { id: 'utilizadores',      label: 'Utilizadores',      icon: Users,         show: showAsAdmin,   adminOnly: true,  grupo: 'admin'      },
    { id: 'atividade',         label: 'Atividade',         icon: Activity,      show: showAsAdmin,   adminOnly: true,  grupo: 'admin'      },
    { id: 'comunicacoes',      label: 'Comunicações',      icon: Send,          show: showAsAdmin,   adminOnly: true,  grupo: 'admin'      },
    { id: 'feedback',          label: 'Feedback',          icon: MessageSquare, show: showAsAdmin,   adminOnly: true,  grupo: 'admin'      },
  ]

  const GRUPO_LABEL = {
    operacao:   'Operação',
    financeiro: 'Financeiro',
    analise:    'Análise',
    admin:      'Administração',
  }

  // ── Sidebar (renderizada no desktop e no drawer mobile) ────────────────────
  const renderSidebar = (closeOnClick = false) => {
    const visiveis = navItems.filter(n => n.show)
    const grupos = []
    visiveis.forEach(item => {
      const last = grupos[grupos.length - 1]
      if (!last || last.id !== item.grupo) {
        grupos.push({ id: item.grupo, items: [item] })
      } else {
        last.items.push(item)
      }
    })

    return (
      <>
        {/* Logo */}
        <div style={{
          padding: sidebarCollapsed && !isMobile ? '20px 12px' : '20px 18px',
          borderBottom: `1px solid ${C.sidebarBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed && !isMobile ? 'center' : 'space-between',
          gap: '10px',
        }}>
          {(!sidebarCollapsed || isMobile) ? (
            <img src={LOGO_LIGHT} alt="LogiStock Pro"
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex' }}
              style={{ height: '28px', width: 'auto', objectFit: 'contain' }} />
          ) : null}
          <div style={{ display: 'none', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <BarChart2 size={15} color={C.black || '#000'} />
            </div>
            {(!sidebarCollapsed || isMobile) && (
              <span style={{ fontWeight: '700', fontSize: '14px', color: C.sidebarText, letterSpacing: '0.02em' }}>
                LogiStock<span style={{ color: C.accent }}>Pro</span>
              </span>
            )}
          </div>
          {isMobile && (
            <button onClick={() => setMobileMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sidebarMuted, padding: '4px', display: 'flex' }}>
              <X size={18} />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '14px 10px', overflowY: 'auto' }}>
          {grupos.map(g => (
            <div key={g.id} style={{ marginBottom: '14px' }}>
              {(!sidebarCollapsed || isMobile) && (
                <div style={{
                  fontSize: '10px', fontWeight: '700', color: C.sidebarMuted,
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  padding: '0 12px 8px',
                }}>{GRUPO_LABEL[g.id]}</div>
              )}
              {g.items.map(({ id, label, icon: Icon, adminOnly }) => {
                const activo = activePage === id
                return (
                  // <a> em vez de <button>: dá ao browser um destino real, e é isso
                  // que faz funcionar o Ctrl+clique, o clique do meio e o "abrir em
                  // nova janela" do menu do botão direito. Um <button> não tem URL,
                  // por isso o browser não tinha o que abrir.
                  // No clique normal continuamos a navegar por dentro (sem recarregar);
                  // só nos cliques com modificador é que deixamos o browser tratar.
                  <a key={id}
                    href={`#${id}`}
                    onClick={e => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                      e.preventDefault()
                      setActivePage(id)
                      if (closeOnClick) setMobileMenuOpen(false)
                    }}
                    title={sidebarCollapsed && !isMobile ? label : ''}
                    style={{
                      width: '100%', textAlign: 'left', boxSizing: 'border-box',
                      textDecoration: 'none',
                      padding: sidebarCollapsed && !isMobile ? '10px' : '10px 12px',
                      height: '40px',
                      border: 'none', borderRadius: '8px',
                      background: activo ? C.sidebarActiveBg : 'transparent',
                      color: activo ? C.sidebarActiveText : C.sidebarText,
                      fontWeight: activo ? '700' : '500',
                      fontSize: '13px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center',
                      gap: sidebarCollapsed && !isMobile ? '0' : '12px',
                      justifyContent: sidebarCollapsed && !isMobile ? 'center' : 'flex-start',
                      marginBottom: '2px',
                      transition: 'background 0.12s, color 0.12s',
                      letterSpacing: '0.01em',
                    }}
                    onMouseEnter={e => { if (!activo) e.currentTarget.style.background = C.sidebarAlt }}
                    onMouseLeave={e => { if (!activo) e.currentTarget.style.background = 'transparent' }}
                  >
                    <Icon size={16} style={{ flexShrink: 0 }} />
                    {(!sidebarCollapsed || isMobile) && (
                      <>
                        <span style={{ flex: 1 }}>{label}</span>
                        {adminOnly && !activo && (
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: C.accent, flexShrink: 0 }} />
                        )}
                      </>
                    )}
                  </a>
                )
              })}
            </div>
          ))}
        </nav>

        {/* Footer sidebar */}
        <div style={{ padding: sidebarCollapsed && !isMobile ? '12px 8px' : '12px 16px', borderTop: `1px solid ${C.sidebarBorder}` }}>
          {/* Instalar app — visível na sidebar/drawer (canto bem visível em mobile) */}
          {(!sidebarCollapsed || isMobile) && (
            <div style={{ marginBottom: '10px' }}>
              <PwaInstallBanner />
            </div>
          )}
          {(!sidebarCollapsed || isMobile) && (
            <div style={{ fontSize: '10px', color: C.sidebarMuted, lineHeight: '1.6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '2px' }}>
                <Shield size={9} /> <span style={{ letterSpacing: '0.06em' }}>SUPABASE · TLS</span>
              </div>
              {isAdmin ? (
                <button onClick={() => setVersoesModalOpen(true)} title="Ver histórico" style={{ background: C.sidebarAlt, border: `1px solid ${C.sidebarBorder}`, color: C.sidebarText, fontSize: '10px', cursor: 'pointer', padding: '2px 8px', borderRadius: '8px', fontWeight: '700', letterSpacing: '0.04em' }}>
                  v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'}
                </button>
              ) : (
                <span style={{ letterSpacing: '0.04em' }}>v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'}</span>
              )}
            </div>
          )}
        </div>

        {/* Toggle collapse — só desktop */}
        {!isMobile && (
          <button onClick={() => setSidebarCollapsed(v => !v)}
            title={sidebarCollapsed ? 'Expandir' : 'Recolher'}
            style={{ position: 'absolute', right: '-12px', top: '70px', width: '24px', height: '24px', borderRadius: '50%', background: C.sidebar, border: `1px solid ${C.sidebarBorder}`, color: C.sidebarText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, boxShadow: '0 2px 6px rgba(0,0,0,0.18)' }}>
            <ChevronRight size={13} style={{ transform: sidebarCollapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s' }} />
          </button>
        )}
      </>
    )
  }

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: FONT, backgroundColor: C.bg, color: C.text }}>

      {/* ── SIDEBAR (desktop) ── */}
      {!isMobile && (
        <aside style={{
          position: 'relative',
          width: sidebarCollapsed ? '68px' : '232px',
          background: C.sidebar,
          color: C.sidebarText,
          display: 'flex', flexDirection: 'column',
          flexShrink: 0,
          transition: 'width 0.2s',
        }}>
          {renderSidebar()}
        </aside>
      )}

      {/* ── DRAWER MOBILE ── */}
      {isMobile && mobileMenuOpen && (
        <>
          <div onClick={() => setMobileMenuOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 400 }} />
          <aside style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '78%', maxWidth: '300px', background: C.sidebar, color: C.sidebarText, zIndex: 401, boxShadow: '4px 0 24px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column' }}>
            {renderSidebar(true)}
          </aside>
        </>
      )}

      {/* ── ÁREA PRINCIPAL ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

        {/* TOP BAR */}
        <header style={{
          height: '56px', flexShrink: 0,
          backgroundColor: C.surface,
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center',
          padding: isMobile ? '0 12px' : '0 20px',
          gap: '10px', zIndex: 100,
        }}>
          {isMobile && (
            <button onClick={() => setMobileMenuOpen(true)} title="Menu"
              style={{ width: '38px', height: '38px', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.surface, cursor: 'pointer', color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>
              <Menu size={18} />
            </button>
          )}

          {/* Título da página activa */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '700', color: C.text, letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {navItems.find(n => n.id === activePage)?.label || 'LogiStock Pro'}
            </div>
            {viewAsEmail && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', backgroundColor: C.accent, color: C.accentText, borderRadius: '999px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em' }}>
                <Eye size={12} />
                <span style={{ whiteSpace: 'nowrap', maxWidth: isMobile ? '120px' : 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}>Vendo: {viewAsEmail}</span>
                <button onClick={() => window.location.href = '/'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accentText, padding: '0', display: 'flex' }}>
                  <X size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Acções globais */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isAdmin && !isMobile && (
              <button onClick={() => setCmdkOpen(true)} title="Pesquisa global (Ctrl+K)"
                style={{ height: '36px', padding: '0 12px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                <Search size={14} />
                <span>Pesquisar</span>
                <kbd style={{ fontSize: '10px', padding: '1px 5px', background: C.surfaceAlt, borderRadius: '4px', border: `1px solid ${C.border}`, fontFamily: 'inherit', color: C.textMuted }}>Ctrl K</kbd>
              </button>
            )}

            {/* Carrinho — visível em qualquer página. O cliente andava a adicionar na
                Gestão de Stock sem confirmação nenhuma de que os artigos lá estavam. */}
            {(showAsCliente || carrinhoQtd > 0) && (
              <button
                onClick={() => {
                  try { sessionStorage.setItem('autoOpenCart', '1') } catch { /* silent */ }
                  setActivePage('catalogo')
                }}
                title={carrinhoQtd > 0 ? `${carrinhoQtd} artigo(s) no carrinho — clica para abrir` : 'Carrinho vazio'}
                style={{
                  position: 'relative', width: '38px', height: '38px',
                  background: C.surface, border: `1px solid ${carrinhoQtd > 0 ? C.accent : C.border}`,
                  borderRadius: '50%', cursor: 'pointer',
                  color: carrinhoQtd > 0 ? C.text : C.textMuted,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <ShoppingCart size={15} />
                {carrinhoQtd > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', right: '-2px', minWidth: '18px', height: '18px', padding: '0 4px', borderRadius: '9px', background: C.accent, color: C.accentText, fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${C.surface}` }}>
                    {carrinhoQtd > 99 ? '99+' : carrinhoQtd}
                  </span>
                )}
              </button>
            )}

            {/* Sino notificações */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setNotifAberto(v => !v)}
                title={notifNaoLidas > 0 ? `${notifNaoLidas} notificação(ões) por ler` : 'Notificações'}
                style={{ position: 'relative', width: '38px', height: '38px', background: notifAberto ? C.surfaceAlt : C.surface, border: `1px solid ${C.border}`, borderRadius: '50%', cursor: 'pointer', color: notifAberto ? C.text : C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Bell size={15} />
                {notifNaoLidas > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', right: '-2px', minWidth: '18px', height: '18px', padding: '0 4px', borderRadius: '9px', background: C.accent, color: C.accentText, fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${C.surface}` }}>
                    {notifNaoLidas > 99 ? '99+' : notifNaoLidas}
                  </span>
                )}
              </button>

              {notifAberto && (
                <>
                  <div onClick={() => setNotifAberto(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 200 }} />
                  <div style={isMobile
                    ? { position: 'fixed', top: '56px', left: 0, right: 0, bottom: 0, width: '100vw', maxHeight: 'none', background: C.surface, borderTop: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 300, display: 'flex', flexDirection: 'column' }
                    : { position: 'absolute', top: '46px', right: 0, width: '380px', maxHeight: '480px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', boxShadow: '0 20px 50px rgba(0,0,0,0.18)', zIndex: 300, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: C.text, letterSpacing: '0.02em' }}>Notificações</span>
                      {notifNaoLidas > 0 && (
                        <button onClick={marcarTodasLidas}
                          style={{ background: 'none', border: 'none', color: C.text, fontSize: '11px', cursor: 'pointer', padding: 0, fontWeight: '700', textDecoration: 'underline' }}>
                          Marcar todas como lidas
                        </button>
                      )}
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {notificacoes.length === 0 ? (
                        <div style={{ padding: '32px 16px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>
                          <Bell size={32} style={{ opacity: 0.25, marginBottom: '8px' }} />
                          <p style={{ margin: 0 }}>Sem notificações</p>
                        </div>
                      ) : notificacoes.map(n => {
                        const tipoIcon =
                          n.tipo === 'encomenda' ? <ShoppingCart size={14} /> :
                          n.tipo === 'feedback' ? <Send size={14} /> :
                          n.tipo === 'utilizador' ? <Users size={14} /> :
                          n.tipo === 'estado_encomenda' ? <Package size={14} /> :
                          n.tipo === 'pagamento' ? <CreditCard size={14} /> :
                          n.tipo === 'stock_esgotado' ? <AlertTriangle size={14} /> :
                          n.tipo === 'stock_critico' ? <AlertTriangle size={14} /> :
                          n.tipo === 'stock_aviso' ? <Package size={14} /> :
                          n.tipo === 'fatura' ? <FileText size={14} /> :
                          n.tipo === 'fatura_falta' ? <AlertTriangle size={14} /> :
                          n.tipo === 'fatura_urgente' ? <AlertTriangle size={14} /> :
                          n.tipo === 'pedir_2a_tranche' ? <AlertTriangle size={14} /> :
                          <Bell size={14} />
                        const tipoCor =
                          n.tipo === 'encomenda' ? C.info :
                          n.tipo === 'feedback' ? '#7b1fa2' :
                          n.tipo === 'utilizador' ? C.success :
                          n.tipo === 'estado_encomenda' ? C.warning :
                          n.tipo === 'pagamento' ? C.success :
                          n.tipo === 'stock_esgotado' ? C.danger :
                          n.tipo === 'stock_critico' ? C.danger :
                          n.tipo === 'stock_aviso' ? C.warning :
                          n.tipo === 'fatura' ? C.success :
                          n.tipo === 'fatura_falta' ? C.warning :
                          n.tipo === 'fatura_urgente' ? C.danger :
                          n.tipo === 'pedir_2a_tranche' ? C.danger :
                          C.textMuted
                        const expandida = notifExpandidaId === n.id
                        return (
                          <div key={n.id}
                            onClick={() => {
                              // Alterna expansao inline (nao navega). Se ainda nao lida, marca como lida ao expandir.
                              setNotifExpandidaId(expandida ? null : n.id)
                              if (!expandida && !n.lida) marcarNotifLida(n.id)
                            }}
                            style={{
                              padding: '11px 14px',
                              borderBottom: `1px solid ${C.border}`,
                              cursor: 'pointer',
                              background: n.lida ? C.surface : '#fffbeb',
                              display: 'flex', gap: '10px',
                              transition: 'background 0.12s'
                            }}
                            onMouseEnter={(ev) => ev.currentTarget.style.background = C.surfaceAlt}
                            onMouseLeave={(ev) => ev.currentTarget.style.background = n.lida ? C.surface : '#fffbeb'}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `${tipoCor}1a`, color: tipoCor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {tipoIcon}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  fontSize: '13px',
                                  fontWeight: n.lida ? '500' : '700',
                                  color: C.text,
                                  flex: 1,
                                  ...(expandida
                                    ? { whiteSpace: 'normal', wordBreak: 'break-word' }
                                    : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })
                                }}>{n.titulo}</span>
                                <span style={{ fontSize: '10px', color: C.textMuted, flexShrink: 0 }}>{fmtTempo(n.created_at)}</span>
                              </div>
                              {n.mensagem && (
                                <p style={{
                                  margin: '2px 0 0',
                                  fontSize: '11px',
                                  color: C.textMuted,
                                  lineHeight: '1.4',
                                  ...(expandida
                                    ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                                    : { overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' })
                                }}>{n.mensagem}</p>
                              )}
                              {expandida && (n.link || n.ref_table) && (
                                <button
                                  onClick={(ev) => { ev.stopPropagation(); clicarNotificacao(n) }}
                                  style={{ marginTop: '8px', background: C.primary, color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 10px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  Ir para {n.link === 'compras' ? 'Compras' : n.link === 'catalogo' ? 'Catálogo' : n.link === 'financeiro' ? 'Financeiro' : n.link === 'minhas-encomendas' ? 'Minhas Encomendas' : 'a página'}
                                  <ChevronRight size={11} />
                                </button>
                              )}
                            </div>
                            <button onClick={(ev) => apagarNotificacao(n.id, ev)} title="Apagar"
                              style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '2px', alignSelf: 'flex-start', borderRadius: '4px' }}>
                              <X size={12} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Botão Ideias & Bugs */}
            <button onClick={() => { setFeedbackModal(true); setFeedbackTab(feedbackNovidades > 0 ? 'meus' : 'enviar') }}
              title="Ideias & Bugs"
              style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '999px', padding: isMobile ? '0' : '0 14px', cursor: 'pointer', height: '38px', width: isMobile ? '38px' : 'auto', color: C.text, fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', lineHeight: '1', flexShrink: 0, letterSpacing: '0.04em' }}>
              <MessageSquare size={13} />
              {!isMobile && <span>Ideias & Bugs</span>}
              {feedbackNovidades > 0 && (
                <span style={{ position: 'absolute', top: '-5px', right: '-5px', background: C.accent, color: C.accentText, borderRadius: '10px', minWidth: '18px', height: '18px', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', border: `2px solid ${C.surface}` }}>
                  {feedbackNovidades}
                </span>
              )}
            </button>

            {/* Perfil */}
            <button onClick={() => setShowPerfil(true)} title="Área do perfil"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '999px', padding: '3px 12px 3px 3px', cursor: 'pointer', height: '38px' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: isAdmin ? C.text : C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <User size={14} color={isAdmin ? '#fff' : C.accentText} />
              </div>
              {!isMobile && (
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: C.text, lineHeight: '1.2' }}>{perfil?.nome || user.email}</div>
                  <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{isAdmin ? 'Admin' : 'Cliente'}</div>
                </div>
              )}
            </button>
          </div>
        </header>

        {/* Banner global: admin a encomendar em nome de outro cliente */}
        {isAdmin && encomendarComoEmail && (
          <div style={{ padding: '8px 20px', background: '#fff4ce', borderBottom: '2px solid #c07400', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#92400e', fontWeight: '600' }}>
            <span style={{ fontSize: '14px' }}>⚠</span>
            <span style={{ flex: 1 }}>
              A encomendar em nome de <strong>{encomendarComoPerfil?.nome || encomendarComoEmail}</strong>. Próxima encomenda será gravada como dele(a).
            </span>
            <button
              onClick={sairModoEncomendar}
              title="Sair do modo encomendar como"
              style={{ height: '24px', padding: '0 10px', background: 'transparent', border: '1px solid #c07400', borderRadius: '4px', color: '#92400e', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <X size={12} /> Sair
            </button>
          </div>
        )}

        {/* ── CONTEÚDO ── */}
        <main style={{ flex: 1, display: 'flex', overflow: 'hidden', background: C.bg }}>
          <ErroBoundary>
          {activePage === 'dashboard'          && <Dashboard isReadOnly={showAsCliente} perfil={perfil} setActivePage={setActivePage} viewAsEmail={viewAsEmail} />}
          {activePage === 'catalogo' && (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <Catalogo viewAsEmail={viewAsEmail} />
            </div>
          )}
          {activePage === 'minhas-encomendas' && showAsCliente && <MinhasEncomendas viewAsEmail={viewAsEmail} setActivePage={setActivePage} />}
          {activePage === 'financeiro'        && showAsAdmin && <Financeiro setActivePage={setActivePage} />}
          {activePage === 'compras'           && showAsAdmin && <ComprasFornecedor />}
          {activePage === 'lucro'             && showAsAdmin && (
            <LucroAnalise onVerFactura={(id) => { setAnaliseCompraId(id); setActivePage('analise-factura') }} />
          )}
          {activePage === 'previsao'          && showAsAdmin && <PrevisaoFaturacao />}
          {activePage === 'iva'               && showAsAdmin && <IvaPage />}
          {activePage === 'tesouraria'        && showAsAdmin && <Tesouraria />}
          {activePage === 'comunicacoes'      && showAsAdmin && <Comunicacoes />}
          {activePage === 'termos'            && <div style={{ flex: 1, overflowY: 'auto' }}><TermosUtilizacao /></div>}
          {activePage === 'politica-devolucao' && <div style={{ flex: 1, overflowY: 'auto' }}><PoliticaDevolucao /></div>}
          {activePage === 'precos-cliente'    && showAsAdmin && <PrecosCliente />}
          {activePage === 'historico-precos'  && showAsAdmin && <HistoricoPrecos />}
          {activePage === 'mercado'           && showAsAdmin && <Mercado />}
          {/* O `temPrecosMercado` nao e so para esconder a entrada do menu: as
              paginas tem endereco proprio (#precos-mercado) e sem esta guarda
              qualquer cliente la chegava escrevendo-o a mao. A regra de acesso
              na base tambem o exige agora — sao as duas metades da mesma porta. */}
          {activePage === 'precos-mercado'    && temPrecosMercado && <div style={{ flex: 1, overflowY: 'auto' }}><VigiaMercado modo="cliente" clienteId={inViewAs ? (vistoComo?.id || null) : null} /></div>}
          {activePage === 'analise-factura'   && showAsAdmin && (
            <AnaliseFactura compraInicial={analiseCompraId} onConsumido={() => setAnaliseCompraId(null)}
              onVoltar={() => setActivePage('lucro')} />
          )}
          {/* Devolve um fragmento sem contentor de scroll próprio, e o <main>
              tem overflow:hidden — sem este invólucro a página fica cortada em
              baixo. Só acrescenta o scroll, não muda nada do que se vê. */}
          {activePage === 'utilizadores'      && showAsAdmin && <UserManagement />}
          {activePage === 'atividade'         && showAsAdmin && <Atividade />}
          {activePage === 'feedback'          && showAsAdmin && <Feedback />}
          </ErroBoundary>
        </main>

        {/* FOOTER fino */}
        <footer style={{ height: '28px', backgroundColor: C.sidebar, color: C.sidebarMuted, display: 'flex', alignItems: 'center', padding: '0 16px', flexShrink: 0, gap: '10px', fontSize: '10px', letterSpacing: '0.04em' }}>
          <span>© 2026 LogiStock Pro</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <button onClick={() => setLegalModal('termos')}
            style={{ background: 'none', border: 'none', color: C.sidebarMuted, fontSize: '10px', cursor: 'pointer', padding: 0, letterSpacing: '0.04em' }}>
            Termos
          </button>
          <span style={{ opacity: 0.4 }}>·</span>
          <button onClick={() => setLegalModal('privacidade')}
            style={{ background: 'none', border: 'none', color: C.sidebarMuted, fontSize: '10px', cursor: 'pointer', padding: 0, letterSpacing: '0.04em' }}>
            Privacidade
          </button>
          <PwaInstallBanner />
          <div style={{ flex: 1 }} />
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', opacity: 0.85 }}>
              <Shield size={10} /> <span>Dados protegidos</span>
            </div>
          )}
        </footer>
      </div>

      {/* ══ PAINEL PERFIL ══ */}
      {showPerfil && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500 }} onClick={() => setShowPerfil(false)}>
          <div onClick={e => e.stopPropagation()}
            style={isMobile
              ? { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', maxWidth: '100vw', backgroundColor: C.surface, boxShadow: '-4px 0 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
              : { position: 'absolute', top: 0, right: 0, bottom: 0, width: '380px', backgroundColor: C.surface, borderLeft: `1px solid ${C.border}`, boxShadow: '-12px 0 32px rgba(0,0,0,0.10)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: isAdmin ? C.text : C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User size={20} color={isAdmin ? '#fff' : C.accentText} />
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: C.text }}>{perfil?.nome || user.email}</div>
                  <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em' }}>● {isAdmin ? 'Admin' : 'Cliente'}</div>
                </div>
              </div>
              <button onClick={() => setShowPerfil(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

              <div style={{ marginBottom: '20px', padding: '14px 16px', backgroundColor: C.surfaceAlt, borderRadius: '8px', border: `1px solid ${C.border}` }}>
                <p style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>Informação da Conta</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { label: 'Email', value: user.email },
                    { label: 'NIF', value: perfil?.nif || '—' },
                    { label: 'Tipo NIF', value: perfil?.tipo_nif || '—' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>{label}</div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: C.text, padding: '6px 10px', backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px' }}>{value}</div>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: '11px', color: C.textMuted, margin: '10px 0 0', fontStyle: 'italic' }}>Email e NIF não podem ser alterados. Contacte o administrador se necessário.</p>
              </div>

              {Number(perfil?.desconto_percentagem) > 0 && (
                <div style={{ marginBottom: '20px', padding: '14px 16px', backgroundColor: C.accent + '20', borderRadius: '8px', border: `1px solid ${C.accent}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '15px', fontWeight: '800', color: C.accentText }}>%</span>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: C.text, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>Desconto Personalizado Activo</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: C.text, lineHeight: '1' }}>-{perfil.desconto_percentagem}%</div>
                    <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '3px' }}>Aplicado automaticamente em todos os produtos.</div>
                  </div>
                </div>
              )}

              <p style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Dados de Contacto <span style={{ color: C.danger }}>*</span></p>

              {perfilMsg.texto && (
                <div style={{ marginBottom: '14px', padding: '10px 12px', backgroundColor: perfilMsg.tipo === 'ok' ? C.successSoft : C.dangerSoft, borderLeft: `3px solid ${perfilMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '6px', color: perfilMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {perfilMsg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertTriangle size={13} />} {perfilMsg.texto}
                </div>
              )}

              {/* Confirmacao por codigo. Aparece depois de Guardar; ate ser
                  introduzido, nada foi alterado na base de dados. */}
              {perfilCodigo.aberto && (
                <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: C.surfaceAlt, border: `2px solid ${C.primary}`, borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.text }}>
                    Confirma com o código que te enviámos
                  </p>
                  <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.textMuted, lineHeight: 1.5 }}>
                    Enviámos um código de 6 dígitos para <strong>{perfilCodigo.enviadoPara}</strong>. Válido 10 minutos.
                    Enquanto não o introduzires, <strong>nada foi alterado</strong>.
                  </p>
                  <input
                    value={perfilCodigo.valor}
                    onChange={e => setPerfilCodigo(c => ({ ...c, valor: e.target.value.replace(/\D/g, '').slice(0, 6), erro: '' }))}
                    onKeyDown={e => { if (e.key === 'Enter' && perfilCodigo.valor.length === 6) confirmarCodigoPerfil() }}
                    inputMode="numeric" autoComplete="one-time-code" placeholder="000000" autoFocus
                    style={{ width: '100%', height: '46px', padding: '0 12px', border: `1px solid ${perfilCodigo.erro ? C.danger : C.border}`, borderRadius: '6px', fontSize: '24px', fontWeight: '700', letterSpacing: '10px', textAlign: 'center', fontFamily: 'ui-monospace, monospace', color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                  {perfilCodigo.erro && (
                    <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.danger, fontWeight: '600' }}>{perfilCodigo.erro}</p>
                  )}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                    <button onClick={confirmarCodigoPerfil} disabled={perfilSaving || perfilCodigo.valor.length !== 6}
                      style={{ flex: 1, height: '36px', backgroundColor: C.primary, color: C.primaryText, border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: (perfilSaving || perfilCodigo.valor.length !== 6) ? 'not-allowed' : 'pointer', opacity: (perfilSaving || perfilCodigo.valor.length !== 6) ? 0.6 : 1 }}>
                      {perfilSaving ? 'A confirmar…' : 'Confirmar alteração'}
                    </button>
                    <button onClick={() => setPerfilCodigo({ aberto: false, valor: '', enviadoPara: '', erro: '' })} disabled={perfilSaving}
                      style={{ height: '36px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '6px', backgroundColor: C.surface, color: C.textMuted, fontSize: '13px', cursor: 'pointer' }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {[
                { label: 'Nome / Empresa *', key: 'nome', placeholder: 'Nome completo ou empresa', type: 'text' },
                { label: 'Email *', key: 'email', placeholder: 'email@empresa.com', type: 'email' },
                { label: 'Telefone *', key: 'telefone', placeholder: '+351 912 345 678', type: 'tel' },
                { label: 'Morada *', key: 'morada', placeholder: 'Rua, nº, localidade', type: 'text' },
                { label: 'Código Postal *', key: 'cod_postal', placeholder: '0000-000', type: 'text' },
              ].map(({ label, key, placeholder, type }) => {
                const isEmpty = !perfilForm[key]?.trim()
                // O email deixou de ser editavel pelo cliente. A regra que decide
                // que produtos ele ve compara-o com o campo do produto — quem o
                // mudasse para o de outro cliente ganhava acesso aos produtos
                // desse outro pela API. O trigger na BD tambem ja o recusa; se o
                // campo continuasse editavel, o cliente escrevia, carregava em
                // Guardar, e nada gravava sem dizer porque.
                const soLeitura = key === 'email' && !isAdmin
                return (
                  <div key={key} style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
                    <input type={type} value={perfilForm[key]}
                      onChange={e => { if (soLeitura) return; setPerfilForm(p => ({ ...p, [key]: e.target.value })) }}
                      placeholder={placeholder}
                      readOnly={soLeitura}
                      title={soLeitura ? 'Para alterar o email, contacta-nos.' : undefined}
                      style={{ width: '100%', height: '36px', padding: '0 10px', border: `1px solid ${isEmpty ? C.danger : C.border}`, borderRadius: '6px', fontSize: '13px', color: soLeitura ? C.textMuted : C.text, outline: 'none', boxSizing: 'border-box', backgroundColor: soLeitura ? C.bg : C.surface, cursor: soLeitura ? 'not-allowed' : 'text' }} />
                    {soLeitura && (
                      <p style={{ margin: '3px 0 0', fontSize: '11px', color: C.textMuted }}>
                        O email identifica a tua conta e os teus produtos. Para o alterar, contacta-nos.
                      </p>
                    )}
                    {isEmpty && !soLeitura && <p style={{ margin: '3px 0 0', fontSize: '11px', color: C.danger }}>Campo obrigatório</p>}
                  </div>
                )
              })}

              {isCliente && (
                <div style={{ marginTop: '8px', paddingTop: '20px', borderTop: `1px solid ${C.border}` }}>
                  <p style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <MapPin size={11} /> Moradas Adicionais
                  </p>
                  <p style={{ fontSize: '11px', color: C.textMuted, margin: '0 0 12px', lineHeight: '1.5' }}>
                    Por defeito as encomendas são entregues nos armazéns LOGNOW. Pode guardar moradas adicionais (escritório, lojas) para escolher no checkout em casos pontuais.
                  </p>

                  {moradaMsg.texto && (
                    <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: moradaMsg.tipo === 'ok' ? C.successSoft : C.dangerSoft, borderLeft: `3px solid ${moradaMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '6px', color: moradaMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {moradaMsg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertTriangle size={13} />} {moradaMsg.texto}
                    </div>
                  )}

                  {!moradasLoading && moradas.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                      {moradas.map(m => (
                        <div key={m.id} style={{ border: `1px solid ${C.border}`, borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', background: moradaEditId === m.id ? C.accent + '20' : C.surface }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: C.text }}>{m.etiqueta}</div>
                            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px', display: 'flex', alignItems: 'center', gap: '2px', flexWrap: 'wrap' }}>
                              {m.nome}
                              {m.nome && <CopyButton text={m.nome} size={10} title="Copiar nome" />}
                              {m.nif ? <>· NIF {m.nif}<CopyButton text={m.nif} size={10} title="Copiar NIF" /></> : null}
                            </div>
                            <div style={{ fontSize: '11px', color: C.textMuted }}>{m.morada}</div>
                            <div style={{ fontSize: '11px', color: C.textMuted }}>{m.cod_postal} {m.pais}</div>
                            {m.telefone && (
                              <div style={{ fontSize: '11px', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '2px' }}>
                                {m.telefone}
                                <CopyButton text={m.telefone} size={10} title="Copiar telefone" />
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                            <button onClick={() => editarMorada(m)} title="Editar" style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '4px 6px', cursor: 'pointer', color: C.text }}>
                              <Pencil size={12} />
                            </button>
                            <button onClick={() => eliminarMorada(m.id)} title="Eliminar" style={{ background: 'none', border: `1px solid ${C.danger}`, borderRadius: '6px', padding: '4px 6px', cursor: 'pointer', color: C.danger }}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '12px' }}>
                    <p style={{ fontSize: '11px', fontWeight: '700', color: C.text, margin: '0 0 10px' }}>
                      {moradaEditId ? 'Editar morada' : 'Adicionar nova morada'}
                    </p>
                    {[
                      { label: 'Etiqueta *', key: 'etiqueta', placeholder: 'Ex: Loja Porto, Escritório' },
                      { label: 'Nome / Empresa *', key: 'nome', placeholder: 'Nome' },
                      { label: 'NIF', key: 'nif', placeholder: '123456789' },
                      { label: 'Morada *', key: 'morada', placeholder: 'Rua, n.º' },
                      { label: 'Código Postal *', key: 'cod_postal', placeholder: '0000-000' },
                      { label: 'País *', key: 'pais', placeholder: 'Portugal' },
                      { label: 'Telefone', key: 'telefone', placeholder: '+351 ...' },
                    ].map(({ label, key, placeholder }) => (
                      <div key={key} style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', color: C.textMuted, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
                        <input type="text" value={moradaForm[key]}
                          onChange={e => setMoradaForm(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={placeholder}
                          style={{ width: '100%', height: '32px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', color: C.text, outline: 'none', boxSizing: 'border-box', backgroundColor: C.surface }} />
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                      <button onClick={guardarMorada} disabled={moradaSaving}
                        style={{ flex: 1, height: '32px', backgroundColor: C.primary, color: C.primaryText, border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: moradaSaving ? 'not-allowed' : 'pointer', opacity: moradaSaving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', letterSpacing: '0.04em' }}>
                        {moradaEditId ? <><Save size={11} /> {moradaSaving ? 'A guardar...' : 'Guardar alterações'}</> : <><Plus size={11} /> {moradaSaving ? 'A guardar...' : 'Adicionar morada'}</>}
                      </button>
                      {moradaEditId && (
                        <button onClick={() => { setMoradaForm(EMPTY_MORADA); setMoradaEditId(null); setMoradaMsg({ tipo: '', texto: '' }) }}
                          style={{ height: '32px', padding: '0 10px', background: C.surface, color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                          Cancelar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Instalar app PWA — bloco visível no painel de perfil (mobile e desktop) */}
              <div style={{ marginTop: '8px', paddingTop: '20px', borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Smartphone size={11} /> Instalar como App
                </p>
                <div style={{ padding: '14px', background: C.accent + '20', border: `1px solid ${C.accent}`, borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: C.accent, color: C.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Download size={20} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: C.text, marginBottom: '2px' }}>Instalar LogiStock Pro</div>
                    <p style={{ margin: '0 0 10px', fontSize: '11px', color: C.textMuted, lineHeight: 1.5 }}>
                      Acesso rápido no telemóvel sem barra do browser. Funciona offline e parece uma app nativa.
                    </p>
                    <PwaInstallBanner />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '8px', paddingTop: '20px', borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Lock size={11} /> Alterar Password
                </p>

                {passMsg.texto && (
                  <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: passMsg.tipo === 'ok' ? C.successSoft : C.dangerSoft, borderLeft: `3px solid ${passMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '6px', color: passMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {passMsg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertTriangle size={13} />} {passMsg.texto}
                  </div>
                )}

                {[{ label: 'Nova Password', key: 'nova' }, { label: 'Confirmar Password', key: 'confirmar' }].map(({ label, key }) => (
                  <div key={key} style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
                    <div style={{ position: 'relative' }}>
                      <input type={passShow[key] ? 'text' : 'password'} value={passForm[key]}
                        onChange={e => setPassForm(p => ({ ...p, [key]: e.target.value }))}
                        placeholder="Mínimo 6 caracteres"
                        style={{ width: '100%', height: '36px', padding: '0 36px 0 10px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box', backgroundColor: C.surface }} />
                      <button type="button" onClick={() => setPassShow(p => ({ ...p, [key]: !p[key] }))}
                        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 0, display: 'flex' }}>
                        {passShow[key] ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                ))}

                <button onClick={alterarPassword} disabled={passSaving}
                  style={{ width: '100%', height: '36px', backgroundColor: C.surfaceAlt, color: C.text, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: passSaving ? 'not-allowed' : 'pointer', opacity: passSaving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', letterSpacing: '0.04em' }}>
                  <Lock size={13} /> {passSaving ? 'A alterar...' : 'Alterar Password'}
                </button>
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px', flexShrink: 0, backgroundColor: C.surfaceAlt }}>
              <button onClick={guardarPerfil} disabled={perfilSaving || perfilCodigo.aberto}
                title={perfilCodigo.aberto ? 'Introduz primeiro o código que te enviámos' : ''}
                style={{ flex: 1, height: '38px', backgroundColor: C.primary, color: C.primaryText, border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: (perfilSaving || perfilCodigo.aberto) ? 'not-allowed' : 'pointer', opacity: (perfilSaving || perfilCodigo.aberto) ? 0.55 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', letterSpacing: '0.04em' }}>
                <Save size={13} /> {perfilSaving ? 'A guardar...' : perfilCodigo.aberto ? 'Aguarda o código' : 'Guardar'}
              </button>
              <button onClick={() => { signOut(); setShowPerfil(false) }}
                style={{ height: '38px', padding: '0 14px', border: `1px solid ${C.danger}`, borderRadius: '6px', backgroundColor: 'transparent', color: C.danger, fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', letterSpacing: '0.04em' }}>
                <LogOut size={13} /> Sair
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Boas-Vindas */}
      {showWelcome && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 750, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
             onClick={fecharWelcome}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: C.surface, borderRadius: '14px', width: '100%', maxWidth: '460px', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ padding: '24px 28px', background: C.sidebar, color: C.sidebarText, textAlign: 'center' }}>
              <div style={{ width: '56px', height: '56px', margin: '0 auto 12px', borderRadius: '50%', background: C.accent, color: C.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShoppingCart size={28} />
              </div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', letterSpacing: '0.02em' }}>Bem-vindo{perfil?.nome ? `, ${perfil.nome.split(' ')[0]}` : ''}!</h2>
              <p style={{ margin: '6px 0 0', fontSize: '13px', opacity: 0.85 }}>Aqui está como funciona a plataforma.</p>
            </div>
            <div style={{ padding: '22px 28px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: ShoppingCart, color: C.text,    titulo: 'Procura no Catálogo', texto: 'Filtra por categoria, marca os teus favoritos e ordena pelos mais comprados por ti.' },
                // As condicoes de pagamento nao sao as mesmas para tudo: dependem
                // do prazo de entrega do artigo, e o proprio carrinho ja diz qual
                // e antes de finalizar. Prometer 50/50 a toda a gente era mentir a
                // metade dos clientes.
                { icon: CreditCard,   color: C.warning, titulo: 'Pagamento conforme o artigo', texto: 'Artigos de entrega rápida pagam-se a 100% na compra; os de prazo mais longo em 2 prestações (50% na compra + 50% antes do envio). O carrinho diz-te sempre qual se aplica.' },
                { icon: Truck,        color: C.success, titulo: 'Acompanha o envio', texto: 'Em "Minhas Encomendas" segues estado, fatura e código de tracking clicável.' },
                { icon: Bell,         color: C.info,    titulo: 'Notificações em tempo real', texto: 'Sino no topo + email para cada mudança importante (pagamento, envio, fatura).' },
              ].map(({ icon: Icon, color, titulo, texto }) => (
                <div key={titulo} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: color + '1a', color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={16} />
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: C.text, marginBottom: '2px' }}>{titulo}</div>
                    <div style={{ fontSize: '12px', color: C.textMuted, lineHeight: '1.5' }}>{texto}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '16px 28px 20px', borderTop: `1px solid ${C.border}`, background: C.surfaceAlt }}>
              <button onClick={fecharWelcome}
                      style={{ width: '100%', height: '42px', border: 'none', borderRadius: '8px', background: C.primary, color: C.primaryText, cursor: 'pointer', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', letterSpacing: '0.04em' }}>
                Começar a explorar <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Versões */}
      {versoesModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 750, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
             onClick={() => setVersoesModalOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: C.surface, borderRadius: '12px', width: '100%', maxWidth: '900px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.30)' }}>
            <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surfaceAlt }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GitBranch size={16} color={C.text} /> Histórico de Versões
                {isAdmin && <span style={{ fontSize: '10px', padding: '2px 8px', background: C.accent, color: C.accentText, borderRadius: '10px', fontWeight: '700', letterSpacing: '0.06em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Shield size={9} /> ADMIN</span>}
              </h2>
              <button onClick={() => setVersoesModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
              <Versoes inModal />
            </div>
          </div>
        </div>
      )}

      {/* Relatório de erro (só admin) — apanha o que ficaria escondido na consola */}
      <ErroGlobal />

      {/* Command Palette */}
      {isAdmin && (
        <CommandPalette
          open={cmdkOpen}
          onClose={() => setCmdkOpen(false)}
          onNavigate={(target) => { setActivePage(target.page); setCmdkOpen(false) }}
        />
      )}

      {/* Modal Aviso Inatividade */}
      {idle.showWarning && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 700, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: C.surface, borderRadius: '12px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '420px', boxShadow: '0 24px 60px rgba(0,0,0,0.30)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: C.warningSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Clock size={20} color={C.warning} />
              </div>
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 4px' }}>Sessão prestes a expirar</h2>
                <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Por motivos de segurança, a sua sessão vai terminar.</p>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: '14px', color: C.text, margin: '0 0 6px' }}>
                Vai ser desligado em <strong style={{ color: C.danger, fontSize: '20px' }}>{idle.secondsLeft}s</strong>.
              </p>
              <p style={{ fontSize: '12px', color: C.textMuted, margin: 0 }}>
                Detetámos inatividade há mais de 29 minutos. Clique em "Continuar sessão" para manter-se ligado.
              </p>
            </div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px', backgroundColor: C.surfaceAlt }}>
              <button onClick={() => { signOut() }}
                style={{ flex: 1, height: '38px', border: `1px solid ${C.border}`, borderRadius: '6px', backgroundColor: C.surface, color: C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', letterSpacing: '0.04em' }}>
                <LogOut size={13} /> Sair agora
              </button>
              <button onClick={idle.cancel}
                style={{ flex: 2, height: '38px', border: 'none', borderRadius: '6px', backgroundColor: C.primary, color: C.primaryText, fontSize: '13px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.04em' }}>
                Continuar sessão
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dados Incompletos */}
      {showDadosIncompletos && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 600, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: C.surface, borderRadius: '12px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '420px', boxShadow: '0 24px 60px rgba(0,0,0,0.22)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: C.warningSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={20} color={C.warning} />
              </div>
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 4px' }}>Dados Incompletos</h2>
                <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Por favor, complete o seu perfil</p>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ fontSize: '14px', color: C.text, margin: '0 0 16px', lineHeight: '1.5' }}>
                Para continuar a utilizar o sistema, é necessário preencher os seguintes dados obrigatórios:
              </p>
              <ul style={{ fontSize: '13px', color: C.text, margin: '0 0 16px', paddingLeft: '20px', lineHeight: '1.6' }}>
                <li>Nome / Empresa</li>
                <li>Email</li>
                <li>Telefone</li>
                <li>Morada</li>
                <li>Código Postal</li>
              </ul>
              <button onClick={() => { setShowDadosIncompletos(false); setShowPerfil(true) }}
                style={{ width: '100%', height: '40px', backgroundColor: C.primary, color: C.primaryText, border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', letterSpacing: '0.04em' }}>
                <User size={16} /> Completar Perfil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL LEGAL */}
      {legalModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setLegalModal(null) }}>
          <div style={{ background: C.surface, borderRadius: '12px', width: '100%', maxWidth: '720px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.30)' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: C.text, letterSpacing: '0.02em' }}>
                {legalModal === 'termos' ? 'Termos e Condições' : 'Política de Privacidade'}
              </h2>
              <button onClick={() => setLegalModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '20px 24px', overflowY: 'auto', fontSize: '13px', color: C.text, lineHeight: '1.65' }}>
              {legalModal === 'termos' ? <TermosCondicoes /> : <PoliticaPrivacidade />}
            </div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setLegalModal(null)}
                style={{ height: '38px', padding: '0 18px', background: C.primary, color: C.primaryText, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', letterSpacing: '0.04em' }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FEEDBACK */}
      {feedbackModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setFeedbackModal(false) }}>
          <div style={{ background: C.surface, borderRadius: '12px', width: '100%', maxWidth: '500px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.30)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', background: C.sidebar, color: C.sidebarText, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.12em', color: C.accent }}>Feedback</p>
                <h2 style={{ margin: '3px 0 0', fontSize: '16px', fontWeight: '700', letterSpacing: '0.02em' }}>Sugestões, Bugs e Dúvidas</h2>
              </div>
              <button onClick={() => setFeedbackModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px', borderRadius: '6px', display: 'flex' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt }}>
              <button onClick={() => setFeedbackTab('enviar')}
                style={{ flex: 1, height: '42px', border: 'none', borderBottom: `2px solid ${feedbackTab === 'enviar' ? C.accent : 'transparent'}`, background: 'transparent', color: feedbackTab === 'enviar' ? C.text : C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.04em' }}>
                📝 Enviar feedback
              </button>
              {!isAdmin && (
                <button onClick={() => { setFeedbackTab('meus'); marcarFeedbacksVistos() }}
                  style={{ flex: 1, height: '42px', border: 'none', borderBottom: `2px solid ${feedbackTab === 'meus' ? C.accent : 'transparent'}`, background: 'transparent', color: feedbackTab === 'meus' ? C.text : C.textMuted, fontSize: '13px', fontWeight: '700', cursor: 'pointer', position: 'relative', letterSpacing: '0.04em' }}>
                  📋 Os meus enviados
                  {feedbackNovidades > 0 && feedbackTab !== 'meus' && (
                    <span style={{ marginLeft: '6px', padding: '1px 6px', background: C.danger, color: '#fff', borderRadius: '8px', fontSize: '10px' }}>{feedbackNovidades}</span>
                  )}
                </button>
              )}
            </div>
            {feedbackTab === 'meus' && !isAdmin ? (
              <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
                {meusFeedbacks.length === 0 ? (
                  <div style={{ padding: '30px 20px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>
                    Ainda não enviaste nenhum feedback. Usa a tab "Enviar feedback" para começar.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {meusFeedbacks.map(f => {
                      const est = FEEDBACK_ESTADOS[f.estado] || FEEDBACK_ESTADOS.recebido
                      const tipoEmoji = f.tipo === 'sugestao' ? '💡' : f.tipo === 'bug' ? '🐛' : f.tipo === 'duvida' ? '❓' : '✉️'
                      return (
                        <div key={f.id} style={{ padding: '12px', border: `1px solid ${C.border}`, borderRadius: '8px', background: C.surface }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px' }}>{tipoEmoji}</span>
                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', background: est.bg, color: est.color }}>
                              {est.emoji} {est.label}
                            </span>
                            <span style={{ fontSize: '11px', color: C.textMuted, marginLeft: 'auto' }}>
                              {new Date(f.created_at).toLocaleDateString('pt-PT')}
                            </span>
                          </div>
                          <h4 style={{ margin: '4px 0 4px', fontSize: '13px', fontWeight: '700', color: C.text }}>{f.titulo}</h4>
                          <p style={{ margin: 0, fontSize: '12px', color: C.textMuted, lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{f.descricao}</p>
                          {f.resposta_publica && (
                            <div style={{ marginTop: '10px', padding: '8px 10px', background: est.bg, borderLeft: `3px solid ${est.color}`, borderRadius: '6px' }}>
                              <p style={{ margin: '0 0 4px', fontSize: '10px', fontWeight: '700', color: est.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Resposta da equipa</p>
                              <p style={{ margin: 0, fontSize: '12px', color: C.text, lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{f.resposta_publica}</p>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ) : (
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tipo</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {[
                    { val: 'sugestao', label: '💡 Sugestão', cor: C.success },
                    { val: 'bug', label: '🐛 Bug', cor: C.danger },
                    { val: 'duvida', label: '❓ Dúvida', cor: C.info },
                    { val: 'outro', label: '✉️ Outro', cor: C.textMuted },
                  ].map(opt => (
                    <button key={opt.val} type="button" onClick={() => setFeedbackForm(f => ({ ...f, tipo: opt.val }))}
                      style={{ flex: '1 1 auto', height: '36px', padding: '0 12px', border: `2px solid ${feedbackForm.tipo === opt.val ? opt.cor : C.border}`, borderRadius: '8px', background: feedbackForm.tipo === opt.val ? `${opt.cor}15` : C.surface, color: feedbackForm.tipo === opt.val ? opt.cor : C.textMuted, fontSize: '12px', fontWeight: feedbackForm.tipo === opt.val ? '700' : '500', cursor: 'pointer' }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Título</label>
                <input type="text" value={feedbackForm.titulo}
                  onChange={(e) => setFeedbackForm(f => ({ ...f, titulo: e.target.value }))}
                  placeholder="Resumo curto do que queres reportar"
                  maxLength={200}
                  style={{ width: '100%', height: '38px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Descrição</label>
                <textarea value={feedbackForm.descricao}
                  onChange={(e) => setFeedbackForm(f => ({ ...f, descricao: e.target.value }))}
                  placeholder="Descreve em detalhe — passos para reproduzir o bug, ou o que querias que fosse melhorado…"
                  rows={6} maxLength={5000}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              {feedbackMsg.texto && (
                <div style={{ padding: '10px 12px', background: feedbackMsg.tipo === 'ok' ? C.successSoft : C.dangerSoft, borderLeft: `4px solid ${feedbackMsg.tipo === 'ok' ? C.success : C.danger}`, borderRadius: '6px', color: feedbackMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px' }}>
                  {feedbackMsg.texto}
                </div>
              )}
              <button onClick={enviarFeedback} disabled={feedbackEnviando}
                style={{ height: '42px', background: C.primary, color: C.primaryText, border: 'none', borderRadius: '8px', cursor: feedbackEnviando ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: feedbackEnviando ? 0.7 : 1, letterSpacing: '0.04em' }}>
                {feedbackEnviando ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> A enviar...</> : <><Send size={14} /> Enviar</>}
              </button>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
